import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { OssOther } from 'utils/const.js';
import { WK_APP_BACKGROUND, WK_APP_FOREGROUND } from 'utils/appAudioEvents';

const HOME_BGM_PATHS = ['/home', '/menu', '/personcenter', '/personcenteredit', '/parenthelp', '/rank', '/record', '/checkin', '/yingshi'];
const MUSIC_STORAGE_KEY = 'music';

let sharedAudio = null;

function getSharedAudio() {
  if (!sharedAudio) {
    const audio = new Audio(`${OssOther}/music/main.mp3`);
    audio.loop = true;
    audio.volume = 0.5;
    sharedAudio = audio;
  }
  return sharedAudio;
}

function releaseSharedAudio() {
  if (sharedAudio) {
    sharedAudio.pause();
    sharedAudio.src = '';
    sharedAudio = null;
  }
}

function getMusicEnabled() {
  try {
    const raw = localStorage.getItem(MUSIC_STORAGE_KEY);
    if (raw === null) return true;
    const v = JSON.parse(raw);
    return !!v;
  } catch (e) {
    return true;
  }
}

/**
 * Home 区域背景音乐：home、menu、个人中心、个人中心编辑、育儿顾问、排行榜、学习记录、打卡、影视共用一个 main.mp3，切换不中断。
 * 受 localStorage 'music' 控制（与 Home/Menu 页的喇叭开关一致）。
 */
const HomeBgm = () => {
  const location = useLocation();
  const pathname = location.pathname || '';
  const pathLower = pathname.toLowerCase();
  const isInArea = HOME_BGM_PATHS.some((p) => pathLower === p || pathLower.startsWith(p + '/'));
  /** 进入后台前是否正在播放 Home BGM（用于回到前台时恢复） */
  const wasPlayingBeforeBackgroundRef = useRef(false);

  // 切到系统后台：暂停；回到 App：若之前在播且仍在本区域且用户未关喇叭则继续播
  useEffect(() => {
    const pathInHomeArea = (path) => {
      const pl = (path || '').toLowerCase();
      return HOME_BGM_PATHS.some((p) => pl === p || pl.startsWith(p + '/'));
    };
    const onBg = () => {
      if (!pathInHomeArea(window.location.pathname)) {
        wasPlayingBeforeBackgroundRef.current = false;
        return;
      }
      const audio = getSharedAudio();
      if (audio && !audio.paused && getMusicEnabled()) {
        wasPlayingBeforeBackgroundRef.current = true;
        audio.pause();
      } else {
        wasPlayingBeforeBackgroundRef.current = false;
      }
    };
    const onFg = () => {
      if (!wasPlayingBeforeBackgroundRef.current) return;
      wasPlayingBeforeBackgroundRef.current = false;
      if (!pathInHomeArea(window.location.pathname)) return;
      if (!getMusicEnabled()) return;
      getSharedAudio().play().catch(() => {});
    };
    window.addEventListener(WK_APP_BACKGROUND, onBg);
    window.addEventListener(WK_APP_FOREGROUND, onFg);
    return () => {
      window.removeEventListener(WK_APP_BACKGROUND, onBg);
      window.removeEventListener(WK_APP_FOREGROUND, onFg);
    };
  }, []);

  useEffect(() => {
    if (isInArea) {
      const audio = getSharedAudio();
      if (getMusicEnabled()) audio.play().catch(() => {});
    } else {
      releaseSharedAudio(); // 离开 home/menu 时释放解码缓冲，减轻内存
    }
  }, [isInArea]);

  useEffect(() => {
    const syncPlayPause = () => {
      if (!isInArea) return;
      const audio = getSharedAudio();
      if (getMusicEnabled()) audio.play().catch(() => {});
      else audio.pause();
    };
    window.addEventListener('home-music-toggle', syncPlayPause);
    return () => window.removeEventListener('home-music-toggle', syncPlayPause);
  }, [isInArea]);

  useEffect(() => {
    if (!isInArea) return;
    const audio = getSharedAudio();
    const tryPlay = () => {
      if (audio.paused && getMusicEnabled()) audio.play().catch(() => {});
    };
    const opts = { passive: true, once: true };
    document.addEventListener('touchstart', tryPlay, opts);
    document.addEventListener('click', tryPlay, opts);
    return () => {
      document.removeEventListener('touchstart', tryPlay, opts);
      document.removeEventListener('click', tryPlay, opts);
    };
  }, [isInArea]);

  // 学前须知弹窗打开时直接释放 BGM 以省内存，关闭后恢复（由 Menu 派发事件）
  useEffect(() => {
    const onRelease = () => releaseSharedAudio();
    const onResume = () => {
      if (!isInArea) return;
      if (getMusicEnabled()) getSharedAudio().play().catch(() => {});
    };
    window.addEventListener('home-bgm-pause', onRelease);
    window.addEventListener('home-bgm-resume', onResume);
    return () => {
      window.removeEventListener('home-bgm-pause', onRelease);
      window.removeEventListener('home-bgm-resume', onResume);
    };
  }, [isInArea]);

  return null;
};

export default HomeBgm;
